import '@dropbox/dig-components/dist/index.web.css';
import '@dropbox/dig-fonts/dist/index.min.css';
import '@dropbox/dig-fonts/dist/sharp_grotesk_23.min.css';
import '@dropbox/dig-foundations/dist/index.css';
import '@dropbox/dpc-ia/index.css';
import './App.css';
import './dig-overrides/index.css';
import './translations/i18n';

import {Dropbox} from '@dropbox/api-v2-client';
import {Modal} from '@dropbox/dig-components/modal';
import * as Sentry from '@sentry/react';
import {ApiError} from 'client';
import {getAPIForLoggedInUser} from 'helpers/api';
import {reportAndLogError} from 'helpers/logging';
import {useAtom, useSetAtom} from 'jotai';
import {tryPrivacyConsentInit} from 'lib/initializePrivacyModule';
import {createContext, useEffect, useState} from 'react';
import {RouterProvider} from 'react-router';
import {PrivacyConsentCategories} from 'types/privacyConsent';
import {
  emailToLdap,
  getAuthService,
  getProfileImageUrl,
  isUserDeactivatedApiError,
} from 'utilities';

import {analyticsLogger} from './analytics/analyticsLogger';
import {updateRouterNavigationType} from './analytics/navigationInfo';
import {getAnonymizedRoute} from './analytics/routeInfo';
import {
  analyticsStorageAtom,
  isPrivatePageAtom,
  pulseLoginFailedTimestampStorageAtom,
  pulseTokenAtom,
  pulseUserAtom,
} from './atoms/auth';
import {hashEmail} from './helpers/utils';
import {router} from './routes';

// Initialize before the first RouterProvider render so an initial loader error
// can report the document's POP action before React effects run.
updateRouterNavigationType(router.state.historyAction);

const E2E_PULSE_USER_STORAGE_KEY = 'pulse_e2e_user';

// 100vh and 100vw goes beyond the actual window.innerHeight and
// window.innerWidth values. Instead of 100vh and 100vw, we should
// use var(--full-inner-height) and var(--full-inner-width).
const getFullWindowDimensionsCss = () => {
  return `:root {--full-inner-height:${window.innerHeight}px;--full-inner-width:${window.innerWidth}px}`;
};
const windowDimensionsVariables = document.createElement('style');
windowDimensionsVariables.innerHTML = getFullWindowDimensionsCss();
window.addEventListener('resize', () => {
  windowDimensionsVariables.innerHTML = getFullWindowDimensionsCss();
});
document.body.appendChild(windowDimensionsVariables);

export const DropboxClientContext = createContext<Dropbox | null>(null);

const root = document.getElementById('root');
if (root) Modal.setAppElement(root);

const App = () => {
  const [dropboxClient, setDropboxClient] = useState<Dropbox | null>(null);
  const setIsAuthRequired = useSetAtom(isPrivatePageAtom);
  const setPulseToken = useSetAtom(pulseTokenAtom);
  const [pulseUser, setPulseUser] = useAtom(pulseUserAtom);

  const [loginFailedTimestamp, setLoginFailedTimestamp] = useAtom(
    pulseLoginFailedTimestampStorageAtom
  );

  const [analyticsEnabled, setAnalyticsEnabled] = useAtom(analyticsStorageAtom);

  useEffect(() => {
    return router.subscribe((state) => {
      updateRouterNavigationType(state.historyAction);
    });
  }, []);

  const updateCategoriesCallback = (categories: Partial<PrivacyConsentCategories>) => {
    if (!categories.analytics) {
      setAnalyticsEnabled(false);
      analyticsLogger().disable();
    } else {
      if (!analyticsEnabled) {
        setAnalyticsEnabled(true);
        analyticsLogger().enableLogging();
      } else {
        analyticsLogger().enableLogging();
      }
    }
  };

  useEffect(() => {
    const hydratePersistedE2EUser = () => {
      const rawE2EUser = localStorage.getItem(E2E_PULSE_USER_STORAGE_KEY);
      if (!rawE2EUser || pulseUser) {
        return;
      }

      try {
        setPulseUser(JSON.parse(rawE2EUser));
      } catch {
        localStorage.removeItem(E2E_PULSE_USER_STORAGE_KEY);
      }
    };

    const loadCurUser = async () => {
      let pulseToken = localStorage.getItem('pulse_token');
      let loggedInUser;

      if (pulseToken && pulseToken !== '""') {
        // Only need to fetch the user data
        loggedInUser = await getAuthService().readUserMeApiV1AuthMeGet();
      } else {
        // Fetch both the user data and the token concurrently
        [loggedInUser, pulseToken] = await Promise.all([
          getAuthService().readUserMeApiV1AuthMeGet(),
          getAuthService().getTokenApiV1AuthTokenGet(),
        ]);
        // tempoary fix for security issue. Remove this once token is nullified fom all client
        setPulseToken('true');
      }

      setDropboxClient(await getAPIForLoggedInUser(loggedInUser.token));
      const ldap = emailToLdap(loggedInUser.email);
      setPulseUser({
        ...loggedInUser,
        profile_photo_url: getProfileImageUrl(ldap),
        ldap,
      });

      // Meticulous: record who is logged in so sessions can be searched by user
      // and so that role-driven UI differences aren't flagged as false positives.
      window.Meticulous?.context.recordUserId(loggedInUser.public_account_id ?? ldap);
      window.Meticulous?.context.recordUserEmail(loggedInUser.email);

      // Meticulous: record admin/content role flags -- each one can cause
      // visible UI differences (extra menus, admin panels, editor controls) that
      // would otherwise appear as unexpected diffs between sessions.
      window.Meticulous?.context.recordCustomContext(
        'isTeamAdmin',
        loggedInUser.is_team_admin ?? false
      );
      window.Meticulous?.context.recordCustomContext(
        'isSuperuser',
        loggedInUser.is_superuser ?? false
      );
      window.Meticulous?.context.recordCustomContext(
        'isUserAdmin',
        loggedInUser.is_useradmin ?? false
      );
      window.Meticulous?.context.recordCustomContext(
        'isContentEditor',
        loggedInUser.is_contenteditor ?? false
      );

      Sentry.setUser({
        id: loggedInUser.public_account_id ?? undefined,
        username: ldap,
      });
      setLoginFailedTimestamp(null);

      // Lazily import the analytics client to not impact TTVC
      // const {analyticsLogger} = await import('./analytics/analyticsLogger');
      const hashedEmail = await hashEmail(loggedInUser.email);
      analyticsLogger().enable(hashedEmail);
      analyticsLogger().logEvent('LOGIN_SUCCESS', {email: hashedEmail});
    };

    hydratePersistedE2EUser();
    loadCurUser().catch(async (e) => {
      if (e instanceof ApiError) {
        if (e.status === 401) {
          // auth error, kick them out
          setIsAuthRequired(true);
          setPulseToken('', isUserDeactivatedApiError(e));

          // const {analyticsLogger} = await import('./analytics/analyticsLogger');
          if (
            loginFailedTimestamp &&
            new Date().getTime() - loginFailedTimestamp.getTime() > 1000 * 60
          ) {
            analyticsLogger().logEvent('LOGIN_FAILED', {
              email: 'unknown',
              route: getAnonymizedRoute(),
            });
          } else {
            setLoginFailedTimestamp(new Date());
            analyticsLogger().logEvent('LOGIN_REFRESH', {
              email: 'unknown',
              route: getAnonymizedRoute(),
            });
          }
          return;
        }
        reportAndLogError(e, 'Error loading current user');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dropboxClient && pulseUser) {
      dropboxClient
        .ccpaGenerateCcpaEmailToken({
          raw_email_address: pulseUser?.email,
          is_account_verified: true,
        })
        .then((response) => {
          if (response.result.token) {
            tryPrivacyConsentInit({
              emailProofToken: response.result.token,
              updateCategoriesCallback: updateCategoriesCallback,
            });
          }
        })
        .catch((e) => {
          console.debug('Error generating ccpa email token', e);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropboxClient, pulseUser]);

  return (
    <DropboxClientContext.Provider value={dropboxClient}>
      <RouterProvider router={router} />
    </DropboxClientContext.Provider>
  );
};

export default App;
